import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { initialState } from './member.state';
import { inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AlertService, GET_ERRORS, Member, MEMBER_MESSAGES } from '@ffx/common';
import { ApplicationStore } from '../app-store/app.store';
import { FlagStore } from '../flag-store/flag.store';
import { ProjectStore } from '../project-store/project.store';
import { Router } from '@angular/router';
import { MemberService } from '../services/member/member.service';

export const MemberStore = signalStore(
    { providedIn: 'root' },
    withState(initialState),
    withMethods((store) => {
        const alertService = inject(AlertService);
        const appStore = inject(ApplicationStore);
        const flagStore = inject(FlagStore);
        const projectStore = inject(ProjectStore);
        const router = inject(Router);
        const memberService = inject(MemberService);

        return {
            clearStore(): void {
                patchState(store, {
                    ...initialState
                });
            },
            async getUsersAndRolesByProjectId(projectId: string): Promise<void> {
                patchState(store, { membersLoading: true });
                try {
                    const users = await firstValueFrom(memberService.getUsersAndRolesByProjectId(projectId));
                    patchState(store, {
                        currentProjectUsers: users,
                        membersLoading: false,
                        projectId: projectId
                    });
                } catch {
                    const errorMessage = MEMBER_MESSAGES.GET_MEMBERS_FAILED;
                    alertService.showNegativeAlert(errorMessage);
                    patchState(store, {
                        currentProjectUsers: [],
                        membersLoading: false
                    });
                }
            },
            async updateRole(member: Member, clearStoreNavigateToDashboard: boolean): Promise<void> {
                patchState(store, { memberAdded: false, membersLoading: true });
                try {
                    await firstValueFrom(memberService.updateRole(member));
                    patchState(store, { memberAdded: true, membersLoading: false });
                    // If the user just lowered their own role (i.e. became a 'Member'), then clear all stores and navigate them to the dashboard
                    if (clearStoreNavigateToDashboard) {
                        appStore.clearStore();
                        flagStore.clearStore();
                        projectStore.clearStore();
                        patchState(store, {
                            ...initialState
                        });
                        router.navigate(['/']);
                    }
                } catch {
                    const errorMessage = MEMBER_MESSAGES.UPDATE_ROLE_FAILED;
                    alertService.showNegativeAlert(errorMessage);
                    patchState(store, { membersLoading: false });
                }
            },
            async addMember(member: Member): Promise<void> {
                patchState(store, { memberAdded: false, membersLoading: true });
                const projectId = projectStore.selectedProject()?.id;
                if (!projectId) {
                    alertService.showNegativeAlert(GET_ERRORS.PROJECT_ID);
                    patchState(store, { membersLoading: false });
                    return;
                }

                try {
                    await firstValueFrom(memberService.addMember(member, projectId));
                    patchState(store, { memberAdded: true, membersLoading: false });
                } catch (error: any) {
                    if (
                        error.message === MEMBER_MESSAGES.USER_DOES_NOT_EXIST ||
                        error.message === MEMBER_MESSAGES.USER_EXISTS_ALREADY
                    ) {
                        alertService.showWarningAlert(error.message);
                    } else {
                        alertService.showNegativeAlert(MEMBER_MESSAGES.ADD_MEMBER_FAILED);
                    }
                    patchState(store, { membersLoading: false });
                }
            },
            async leaveProject(projectName: string, userUid: string, projectId: string): Promise<void> {
                patchState(store, { leaveProjectLoading: true });
                try {
                    const projectRoleUserIds = await firstValueFrom(
                        memberService.getProjectRoleUserId(projectId, userUid)
                    );
                    const projectRoleUserId = projectRoleUserIds[0];
                    await firstValueFrom(memberService.deleteMember(projectRoleUserId.id));
                    appStore.clearStore();
                    flagStore.clearStore();
                    projectStore.clearStore();
                    patchState(store, {
                        ...initialState
                    });
                    alertService.showPositiveAlert(`You have successfully left the '${projectName}' project.`);
                    router.navigate(['/']);
                } catch {
                    patchState(store, { leaveProjectLoading: false });
                    const errorMessage = MEMBER_MESSAGES.LEAVE_PROJECT_FAILED;
                    alertService.showNegativeAlert(errorMessage);
                }
            },
            async deleteMember(projectRoleUserId: string, deleteSelf: boolean): Promise<void> {
                patchState(store, { membersLoading: true, memberAdded: false });
                try {
                    await firstValueFrom(memberService.deleteMember(projectRoleUserId));
                    // If the user just deleted themselves, then clear all stores and navigate them to the dashboard
                    if (deleteSelf) {
                        appStore.clearStore();
                        flagStore.clearStore();
                        projectStore.clearStore();
                        patchState(store, {
                            ...initialState
                        });
                        router.navigate(['/']);
                    } else {
                        patchState(store, { memberAdded: true, membersLoading: false });
                    }
                } catch {
                    patchState(store, { membersLoading: false });
                    const errorMessage = MEMBER_MESSAGES.DELETE_MEMBER_FAILED;
                    alertService.showNegativeAlert(errorMessage);
                }
            }
        };
    })
);
